import { Component, computed, DestroyRef, effect, inject, input, model, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { DatePipe, DecimalPipe } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '../../../shared/confirm-dialog/confirm-dialog.component';
import { extractErrorMessage } from '../../../shared/utils/extract-error-message';
import {
  HlmBadgeComponent,
  HlmBtnDirective,
  HlmIconComponent,
  HlmSpinnerComponent,
  ToastService,
} from '../../../ui';
import { SessionService } from '../../../core/session/session.service';
import { Invoice, InvoiceService } from '../invoice.service';
import { UsageCreditService } from './usage-credit.service';
import { Credit, CREDIT_TYPE_EXPIRED, CREDIT_TYPE_GENERAL, creditTypeLabel } from './usage-credit.models';
import { AddCreditDialogComponent, AddCreditDialogData } from './add-credit-dialog/add-credit-dialog.component';
import { CappedListComponent } from '../../../shared/capped-list/capped-list.component';

/**
 * Usage Credit (legacy "Nutzungsguthaben") section card — EB-3883.
 *
 * Self-contained collapsible section card, mirroring `cmt-customer-billing`
 * 1:1: own header (icon chip + h2 + count badge + chevron + Add button), own
 * `collapsed` model, mounted always (NOT behind a parent `@if`) so credits load
 * eagerly on init and the count + invoiceResolved back-fill work regardless of
 * collapse state.
 *
 * Contract with the parent invoice page (mirrors customer-billing):
 *  - input `[invoices]`  — used to resolve a credit row's internal `invoiceId`
 *    to the official `showReId` badge; unresolved ids fall back to "internal {id}".
 *  - output `(jumpToInvoice)` — emits the internal invoice id on badge click;
 *    parent expands + scrolls the Invoices section.
 *  - output `(invoiceResolved)` — back-fills invoices missing from the parent's
 *    list (idempotent on the parent side).
 *  - two-way `collapsed` — lets the page's Collapse-all drive this section.
 */
@Component({
  selector: 'cmt-usage-credit',
  imports: [
    DatePipe,
    DecimalPipe,
    HlmBadgeComponent,
    HlmBtnDirective,
    HlmIconComponent,
    HlmSpinnerComponent,
    CappedListComponent,
  ],
  templateUrl: './usage-credit.component.html',
})
export class UsageCreditComponent {
  private readonly sessionService = inject(SessionService);
  private readonly usageCreditService = inject(UsageCreditService);
  private readonly invoiceService = inject(InvoiceService);
  private readonly toast = inject(ToastService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Customer's invoices (loaded by the parent invoice page). Maps a credit
   * row's internal `invoiceId` to the official `showReId` shown to the
   * customer. Missing entries (very recent invoices absent from the legacy
   * feed) are fetched on demand and emitted via `(invoiceResolved)`.
   */
  readonly invoices = input<Invoice[]>([]);

  /**
   * Emitted whenever a credit references an invoice missing from the
   * `invoices` input and we successfully back-filled it via /invoice/v1.
   * Parent appends the resolved record to its `invoices()` signal.
   */
  readonly invoiceResolved = output<Invoice>();

  /**
   * Emitted when the user clicks an invoice badge on a credit row. The parent
   * expands the Invoices section, filters it by this id and scrolls into view.
   */
  readonly jumpToInvoice = output<number>();

  /** Two-way collapse state so the page Collapse-all toggle can drive it. */
  readonly collapsed = model<boolean>(false);

  readonly loading = signal(false);
  readonly credits = signal<Credit[]>([]);

  /** Track which ids we've already attempted so we don't fire repeated GETs. */
  private readonly resolvedExtraIds = new Set<number>();

  /** In-flight credits load — cancelled before each new fetch (last-write-wins guard). */
  private creditsSub?: Subscription;

  /** Customer id of the most recent load; gates stale responses and the memo reset. */
  private loadedCustomerId: string | null = null;

  /** Lookup map: parent-supplied invoices keyed by internal id → showReId. */
  private readonly invoiceShowReIdMap = computed(() => {
    const m = new Map<number, number>();
    for (const inv of this.invoices()) m.set(inv.id, inv.showReId);
    return m;
  });

  /** Credits sorted by `insertedOn` desc (newest first). */
  readonly sortedCredits = computed(() =>
    [...this.credits()].sort((a, b) => (b.insertedOn ?? '').localeCompare(a.insertedOn ?? '')),
  );

  /** Type-label helpers exposed to the template. */
  readonly creditTypeLabel = creditTypeLabel;

  constructor() {
    // Eager load: reload whenever the session customer changes. Mirrors
    // customer-billing's effect-driven load so the count badge + invoice
    // back-fill work even while the section is collapsed.
    effect(() => {
      const customerId = this.sessionService.customerId();
      if (customerId != null) this.reload(String(customerId));
    });

    // Resolve official invoice numbers for credit rows that reference invoices
    // not present in the parent's loaded list. Fires on every credits/invoices
    // change but triggers at most one network call per missing id.
    effect(() => {
      const known = new Set<number>();
      for (const inv of this.invoices()) known.add(inv.id);
      const missing: number[] = [];
      for (const credit of this.credits()) {
        const id = credit.invoiceId;
        if (id == null) continue;
        if (known.has(id)) continue;
        if (this.resolvedExtraIds.has(id)) continue;
        if (!missing.includes(id)) missing.push(id);
      }
      if (missing.length === 0) return;
      for (const id of missing) {
        this.resolvedExtraIds.add(id);
        this.invoiceService
          .getInvoiceById(id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: (inv) => this.invoiceResolved.emit(inv),
            // Failed lookups stay marked-as-resolved so we don't retry on
            // every row change. The badge falls back to "internal {id}".
            error: () => void 0,
          });
      }
    });
  }

  toggle(): void {
    this.collapsed.update((v) => !v);
  }

  /** Returns the official invoice number (showReId), or `null` if unknown. */
  officialInvoiceNumber(internalId: number | null | undefined): number | null {
    if (internalId == null) return null;
    return this.invoiceShowReIdMap().get(internalId) ?? null;
  }

  /** Type-badge variant: General=secondary, Expired=outline, unknown=null (raw muted). */
  typeBadgeVariant(type: number): 'secondary' | 'outline' | null {
    if (type === CREDIT_TYPE_GENERAL) return 'secondary';
    if (type === CREDIT_TYPE_EXPIRED) return 'outline';
    return null;
  }

  /** A credit is deletable only while it has not been invoiced (BR-1). */
  isDeletable(credit: Credit): boolean {
    return credit.invoiceId === null;
  }

  reload(customerId?: string): void {
    const id =
      customerId ?? (this.sessionService.customerId() != null ? String(this.sessionService.customerId()) : null);

    // Cancel any in-flight load first so a slow earlier response can't clobber a
    // newer customer's list (last-write-wins race — the EB-3694 class, here in
    // the load path).
    this.creditsSub?.unsubscribe();

    if (id == null) {
      this.loadedCustomerId = null;
      this.resolvedExtraIds.clear();
      this.credits.set([]);
      return;
    }

    // Customer changed → drop the resolved-id memo so the invoice back-fill
    // effect can never reference a previous customer's invoices.
    if (id !== this.loadedCustomerId) {
      this.resolvedExtraIds.clear();
      this.loadedCustomerId = id;
    }

    this.loading.set(true);
    this.creditsSub = this.usageCreditService
      .getCredits(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (credits) => {
          // Drop a late response for a customer we've since navigated away from.
          if (id !== this.loadedCustomerId) return;
          this.credits.set(credits);
          this.loading.set(false);
        },
        error: () => {
          if (id !== this.loadedCustomerId) return;
          this.loading.set(false);
          this.credits.set([]);
          this.toast.open('Failed to load usage credits', 'OK', { duration: 5000 });
        },
      });
  }

  /**
   * Open the New Usage Credit Entry dialog; reload the list when it returns a created
   * credit. Customer scope comes from the session (BR-3).
   */
  openAddDialog(): void {
    const customer = this.sessionService.customer();
    if (!customer) {
      this.toast.open('No customer loaded', 'OK', { duration: 3000, tone: 'default' });
      return;
    }
    const ref = this.dialog.open<AddCreditDialogComponent, AddCreditDialogData, Credit | null>(
      AddCreditDialogComponent,
      {
        data: { customerId: customer.id, invoiceDay: customer.invoiceDay ?? null },
        panelClass: 'cmt-hlm-dialog',
        width: '480px',
        autoFocus: false,
        restoreFocus: true,
      },
    );
    ref
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (result) {
          this.reload();
          this.toast.open('Usage credit entry saved', 'OK', { duration: 3000 });
        }
      });
  }

  /**
   * Confirm-then-delete flow. Unlike billing's optimistic removal, a failed
   * DELETE reloads the list (BR / G4) rather than rolling back a hidden row —
   * keeps the UI consistent with the server even for concurrent/invoiced edge
   * cases.
   */
  deleteCredit(credit: Credit): void {
    if (credit.invoiceId !== null) return; // guarded by the UI too

    const label = credit.reason?.trim() || `#${credit.id}`;
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Delete Credit',
        message: `Delete credit "${label}"? This action cannot be undone.`,
        confirmLabel: 'Delete',
        confirmColor: 'warn',
        icon: 'delete',
      },
      panelClass: 'cmt-hlm-dialog',
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed) => {
        if (confirmed) this.finalizeDelete(credit.id);
      });
  }

  private finalizeDelete(id: number): void {
    this.usageCreditService
      .deleteCredit(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.credits.update((credits) => credits.filter((c) => c.id !== id));
          this.toast.open('Credit deleted', 'OK', { duration: 3000 });
        },
        error: (err: unknown) => {
          // Backend refused (e.g. concurrent invoicing / already-invoiced / 404).
          // Surface the server message (EB-3877 pattern), then reload so the list
          // reflects real server state instead of guessing.
          this.toast.open(extractErrorMessage(err, 'Failed to delete credit'), 'OK', { duration: 5000 });
          this.reload();
        },
      });
  }
}
